import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Loader2, Save, Percent, Tag, ShieldAlert, Store } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { apiAdminConfigComercial, apiGuardarConfigComercial } from "../lib/marketplaceApi";
import { humanizeAxiosError } from "../lib/api";

const PLAN_UI = [
  { key: "basico", label: "Básica", tag: "Gratis", dot: "#16A34A", tint: "#DCFCE7", text: "#15803D" },
  { key: "pro", label: "Pro", tag: "Membresía PRO", dot: "#2563EB", tint: "#DBEAFE", text: "#1D4ED8" },
  { key: "elite", label: "Premium", tag: "Membresía Premium", dot: "#7C3AED", tint: "#EDE9FE", text: "#6D28D9" },
];

export default function AdminMarketplaceConfig() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [comm, setComm] = useState({ basico: "", pro: "", elite: "" });
  const [minPrice, setMinPrice] = useState("");

  useEffect(() => {
    apiAdminConfigComercial()
      .then((d) => {
        setComm({
          basico: d.commissions?.basico ?? "",
          pro: d.commissions?.pro ?? "",
          elite: d.commissions?.elite ?? "",
        });
        setMinPrice(d.minimum_sale_price ?? "");
      })
      .catch(() => toast.error("No se pudo cargar la configuración."))
      .finally(() => setLoading(false));
  }, []);

  const guardar = async () => {
    setSaving(true);
    try {
      const d = await apiGuardarConfigComercial({
        basic_commission: Number(comm.basico),
        pro_commission: Number(comm.pro),
        elite_commission: Number(comm.elite),
        minimum_sale_price: Number(minPrice),
      });
      setComm({ basico: d.commissions.basico, pro: d.commissions.pro, elite: d.commissions.elite });
      setMinPrice(d.minimum_sale_price);
      toast.success("Configuración comercial actualizada.");
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-lg mx-auto text-center py-20" data-testid="admin-denegado">
        <ShieldAlert size={40} className="mx-auto text-brand-red" />
        <h2 className="mt-4 text-xl font-bold text-slate-900">Acceso restringido</h2>
        <Link to="/" className="mt-4 inline-block text-brand-blue font-semibold hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <div className="px-5 md:px-8 py-8 max-w-3xl mx-auto" data-testid="admin-mkt-config-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Panel
      </Link>
      <header className="mt-4 mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-blue">Marketplace</p>
        <h1 className="mt-1 text-3xl font-extrabold text-slate-900 tracking-tight">Configuración Comercial</h1>
        <p className="mt-1 text-slate-500 text-sm">Comisiones por membresía y precio mínimo de venta. Los cambios aplican a publicaciones y ventas futuras; las ventas históricas conservan su comisión original.</p>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="animate-spin mr-2" /> Cargando…</div>
      ) : (
        <div className="space-y-6">
          {/* Comisiones */}
          <section className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5">
            <div className="flex items-center gap-2 mb-4">
              <Percent size={18} className="text-brand-blue" />
              <h2 className="font-bold text-slate-900">Comisión por membresía (%)</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {PLAN_UI.map((p) => (
                <div key={p.key} className="rounded-xl border p-4" style={{ borderColor: p.dot }} data-testid={`cfg-plan-${p.key}`}>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: p.tint, color: p.text }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.dot }} /> {p.label}
                  </span>
                  <p className="text-[11px] text-slate-400 mt-1">{p.tag}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      type="number" min="0" max="100" step="0.1"
                      value={comm[p.key]}
                      onChange={(e) => setComm((c) => ({ ...c, [p.key]: e.target.value }))}
                      className="w-full h-11 px-3 rounded-xl border border-slate-200 text-lg font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                      data-testid={`cfg-input-${p.key}`}
                    />
                    <span className="text-slate-400 font-bold">%</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Precio mínimo */}
          <section className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5">
            <div className="flex items-center gap-2 mb-4">
              <Tag size={18} className="text-brand-blue" />
              <h2 className="font-bold text-slate-900">Precio mínimo de venta</h2>
            </div>
            <div className="flex items-center gap-2 max-w-xs">
              <span className="text-slate-500 font-bold">S/</span>
              <input
                type="number" min="0" step="0.01"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className="w-full h-11 px-3 rounded-xl border border-slate-200 text-lg font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                data-testid="cfg-min-price"
              />
            </div>
            <p className="text-xs text-slate-400 mt-2">Los vendedores no podrán publicar por debajo de este monto (no aplica a cursos gratuitos).</p>
          </section>

          <button onClick={guardar} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-brand-blue text-white font-bold px-6 py-3 hover:brightness-110 transition disabled:opacity-60" data-testid="cfg-guardar-btn">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Guardar cambios
          </button>
        </div>
      )}
    </div>
  );
}
